import { useState } from "react";
import { useLocation } from "wouter";
import { CalendarDays, ExternalLink, Film, FolderOpen, Image as ImageIcon } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { formatDate } from "@/lib/dateUtils";
import { formatMediaBytes } from "@shared/svrMediaPolicy";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

type CustomerMediaItem = {
  id: number;
  svrId: number;
  visitNumber: string;
  visitDate: Date | string;
  visitStatus: string;
  complaintDescription: string | null;
  s3Url: string;
  mimeType: string | null;
  byteSize: number | null;
  caption: string | null;
  createdAt: Date | string;
};

function isVideo(item: CustomerMediaItem): boolean {
  return String(item.mimeType ?? "").toLowerCase().startsWith("video/");
}

function MediaPreview({
  item,
  className,
  controls = false,
}: {
  item: CustomerMediaItem;
  className: string;
  controls?: boolean;
}) {
  if (isVideo(item)) {
    return <video src={item.s3Url} controls={controls} muted={!controls} preload="metadata" className={className} />;
  }
  return <img src={item.s3Url} alt={item.caption || `Media from ${item.visitNumber}`} className={className} />;
}

export function CustomerMediaSection({ customerId, enabled = true }: { customerId: number; enabled?: boolean }) {
  const [, navigate] = useLocation();
  const [selected, setSelected] = useState<CustomerMediaItem | null>(null);
  const mediaQ = trpc.customers.listSvrMedia.useQuery({ customerId }, { enabled });
  const media = (mediaQ.data ?? []) as CustomerMediaItem[];

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          <FolderOpen className="h-4 w-4" />
          Customer Media ({media.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        {mediaQ.isLoading ? (
          <p className="py-8 text-center text-sm text-muted-foreground">Loading media…</p>
        ) : media.length === 0 ? (
          <div className="py-10 text-center">
            <FolderOpen className="mx-auto mb-2 h-9 w-9 text-muted-foreground" />
            <p className="text-sm font-medium">No customer media yet</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Photos and videos uploaded through Cleo during service visits will appear here.
            </p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {media.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setSelected(item)}
                className="overflow-hidden rounded-lg border bg-white text-left transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#1a2744]"
              >
                <div className="relative bg-slate-100">
                  <MediaPreview item={item} className="aspect-video w-full object-cover" />
                  <span className="absolute right-2 top-2 rounded-full bg-[#1a2744]/90 p-1.5 text-white">
                    {isVideo(item) ? <Film className="h-3.5 w-3.5" /> : <ImageIcon className="h-3.5 w-3.5" />}
                  </span>
                </div>
                <div className="space-y-1.5 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-xs font-semibold text-[#1a2744]">{item.visitNumber}</span>
                    <span className="text-[11px] text-muted-foreground">{formatMediaBytes(item.byteSize)}</span>
                  </div>
                  <p className="line-clamp-2 text-sm">{item.caption || item.complaintDescription || "Site visit media"}</p>
                  <p className="flex items-center gap-1 text-xs text-muted-foreground">
                    <CalendarDays className="h-3 w-3" /> Uploaded {formatDate(item.createdAt)}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={Boolean(selected)} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
          {selected ? (
            <>
              <DialogHeader>
                <DialogTitle>{selected.visitNumber} · {formatDate(selected.visitDate)}</DialogTitle>
              </DialogHeader>
              <MediaPreview item={selected} controls className="max-h-[62vh] w-full rounded-md bg-black object-contain" />
              <div className="rounded-md bg-slate-50 p-3 text-sm">
                <p>{selected.caption || selected.complaintDescription || "No caption supplied."}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Uploaded {formatDate(selected.createdAt)} · {formatMediaBytes(selected.byteSize)}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => navigate(`/site-visits/id/${selected.svrId}`)}>
                  <ExternalLink className="mr-2 h-4 w-4" /> Open in SVR
                </Button>
                <Button variant="outline" asChild>
                  <a href={selected.s3Url} target="_blank" rel="noreferrer">Open original</a>
                </Button>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
